import { runInAction } from 'mobx';

import type { IStoredPredicate, TPredicateKind } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import type { IBaseAttributes, Model } from '@feathr/rachis';

export type TPredicatesModel = Model<IBaseAttributes & { predicates?: IStoredPredicate[] }>;

/**
 * The default shape for a fresh filter or group rule: activity rules start on
 * the visited-URL activity, anything else on the last-seen date attribute.
 */
export function createDefaultRule(kind?: TPredicateKind): IStoredPredicate {
  const isActivity = kind === 'activity';
  return ensurePredicateKeys({
    kind,
    attr_against: isActivity ? 'loc_url' : 'date_last_seen',
    attr_type: isActivity ? 'string' : 'date',
    comparison: 'eq',
    value: '',
    unit: 'days',
    group: [],
  });
}

/*
 * Every write swaps a NEW list into model.set: set() deep-diffs the incoming value against the
 * model's attributes, so mutating the stored list and then passing it back to set() would compare
 * equal and never mark the attribute dirty.
 */
export function writePredicates(model: TPredicatesModel, predicates: IStoredPredicate[]): void {
  runInAction(() => {
    model.set({ predicates });
  });
}

/** Appends a predicate to the model's list. */
export function addPredicate(model: TPredicatesModel, predicate: IStoredPredicate): void {
  writePredicates(model, [...(model.get('predicates') ?? []), predicate]);
}

/**
 * Swaps the edited clone in for the row carrying its key, resolved against the model's list as
 * it is when the call RUNS: a deferred call (e.g. a debounced value input flushing after other
 * rows changed) can't clobber rows added, edited, or removed in the meantime, and an edit for
 * a row that no longer exists is dropped.
 */
export function updatePredicate(model: TPredicatesModel, edited: IStoredPredicate): void {
  const predicates = model.get('predicates') ?? [];
  const index = predicates.findIndex((predicate) => predicate.key === edited.key);
  if (index === -1) {
    return;
  }
  writePredicates(
    model,
    predicates.map((predicate, i) => (i === index ? edited : predicate)),
  );
}

/** Removes the row with `key`; a deferred call for an already-gone row writes nothing. */
export function removePredicate(model: TPredicatesModel, key: string): void {
  const predicates = model.get('predicates') ?? [];
  const remaining = predicates.filter((predicate) => predicate.key !== key);
  if (remaining.length === predicates.length) {
    return;
  }
  writePredicates(model, remaining);
}
